import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Dialog,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Badge,
  Fab,
  Avatar,
  Tooltip,
  Zoom,
  Slide,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';
import {
  QrCodeScanner,
  Science,
  Assignment,
  Schedule,
  Warning,
  PriorityHigh,
  Visibility,
  Analytics,
  TrendingUp,
  Speed,
  Biotech,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import usersService, { User } from '../../services/usersService';
import AnalysisForm from '../forms/AnalysisForm';
import WorkflowQRScanner from '../WorkflowQRScanner';
import ProgressiveWorkflowDisplay from '../ProgressiveWorkflowDisplay';

const AnalystDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [analysts, setAnalysts] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAnalysisForm, setShowAnalysisForm] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [stats, setStats] = useState({
    ready_for_analysis: 0,
    in_analysis: 0,
    urgent_samples: 0,
  });

  useEffect(() => {
    loadAnalysisData();
  }, []);

  const loadAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load samples that are ready for analysis in any laboratory
      // Enhanced workflow uses laboratory-specific analysis statuses
      const analysisStatuses = [
        SampleStatus.LABORATOIRE_SAA_ANALYSE,
        SampleStatus.LABORATOIRE_ICP_ANALYSE,
        SampleStatus.LABORATOIRE_AU_ANALYSE,
        SampleStatus.LABORATOIRE_VOLUMETRIE_ANALYSE,
        // Keep backward compatibility with old status
        SampleStatus.ANALYSE_CHIMIQUE
      ];
      
      const [analysisResponse, analystsData] = await Promise.all([
        samplesService.getSamples(1, 100, {
          status: analysisStatuses,
        }),
        usersService.getAnalysts()
      ]);
      
      // Filter to ensure we only have samples in laboratory analysis statuses
      const filteredSamples = analysisResponse.samples.filter(sample => 
        analysisStatuses.includes(sample.statut)
      );
      
      setSamples(filteredSamples);
      setAnalysts(analystsData);
      
      // Calculate stats from real data
      const urgentSamples = filteredSamples.filter(
        sample => sample.priorite === Priority.URGENTE
      ).length;
      
      setStats({
        ready_for_analysis: filteredSamples.length,
        in_analysis: filteredSamples.length, // Same as ready_for_analysis since they're all in analysis status
        urgent_samples: urgentSamples,
      });
      
      if (filteredSamples.length !== analysisResponse.samples.length) {
        console.warn('Some samples were filtered out due to incorrect status in analyst dashboard');
      }
    } catch (error) {
      console.error('Error loading analysis data:', error);
      setError('Erreur lors du chargement des données d\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleQRScanSuccess = async (sample: Sample) => {
    try {
      setSelectedSample(sample);
      setShowQRScanner(false);
      setShowAnalysisForm(true);
    } catch (error) {
      console.error('Error loading sample:', error);
      setError('Échantillon non trouvé ou erreur de chargement');
    }
  };

  const handleAnalysisCompleted = async (data: any, action: 'SAVE_DRAFT' | 'SAVE_RESULTS' | 'FINALIZE_ANALYSIS') => {
    try {
      if (selectedSample) {
        let newStatus = selectedSample.statut;
        let statusMessage = '';
        
        // Determine status based on action
        switch (action) {
          case 'SAVE_DRAFT':
            // Keep current status, just save data
            statusMessage = 'Brouillon d\'analyse sauvegardé';
            break;
          case 'SAVE_RESULTS':
            newStatus = SampleStatus.ANALYSE_CHIMIQUE;
            statusMessage = 'Résultats d\'analyse sauvegardés';
            break;
          case 'FINALIZE_ANALYSIS':
            // Analysis completion - keep in ANALYSE_CHIMIQUE but set completion date
            // This will make it appear in validation queue
            newStatus = SampleStatus.ANALYSE_CHIMIQUE;
            statusMessage = 'Analyse chimique finalisée - En attente de validation';
            break;
        }
        
        // Update sample status if changed
        if (newStatus !== selectedSample.statut) {
          await samplesService.updateSampleStatus(
            selectedSample.id, 
            newStatus,
            statusMessage
          );
        }
        
        // Always update sample with analysis data
        await samplesService.updateSample(selectedSample.id, {
          date_fin_analyse: action === 'FINALIZE_ANALYSIS' ? new Date().toISOString() : undefined,
          metadonnees: {
            ...selectedSample.metadonnees,
            analysis_data: data,
            analysis_status: action,
            last_updated: new Date().toISOString()
          }
        });
      }
      
      setShowAnalysisForm(false);
      setSelectedSample(null);
      loadAnalysisData();
    } catch (error) {
      console.error('Error handling analysis:', error);
      setError('Erreur lors de la sauvegarde de l\'analyse');
    }
  };

  const handleSampleClick = (sample: Sample) => {
    setSelectedSample(sample);
    setShowAnalysisForm(true);
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return 'error';
      case Priority.ELEVEE:
        return 'warning';
      case Priority.NORMALE:
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return <PriorityHigh />;
      case Priority.ELEVEE:
        return <Warning />;
      default:
        return <Schedule />;
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          borderRadius: 3,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Chargement des données d'analyse...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.02)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      minHeight: '100vh',
    }}>
      {/* Modern Header with Glassmorphism */}
      <Fade in timeout={800}>
        <Card sx={{ 
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          borderRadius: 4,
          overflow: 'visible',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
            borderRadius: '16px 16px 0 0',
          }
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  Bonjour, {user?.firstName} 🧪
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                  Tableau de bord - Analyse chimique des échantillons
                </Typography>
              </Box>
              <Avatar 
                sx={{ 
                  width: 80, 
                  height: 80,
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                  fontSize: '2rem',
                  fontWeight: 'bold',
                }}
              >
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {error && (
        <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Slide>
      )}

      {/* Modern Stats Cards with Animations */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            icon: Assignment,
            value: stats.ready_for_analysis,
            label: 'Prêts pour analyse',
            color: theme.palette.secondary.main,
            gradient: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
            delay: 200,
          },
          {
            icon: Science,
            value: stats.in_analysis,
            label: 'En analyse',
            color: theme.palette.primary.main,
            gradient: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            delay: 400,
          },
          {
            icon: PriorityHigh,
            value: stats.urgent_samples,
            label: 'Échantillons urgents',
            color: theme.palette.error.main,
            gradient: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            delay: 600,
          },
        ].map((stat, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Zoom in timeout={stat.delay}>
              <Card sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 4,
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(stat.color, 0.3)}`,
                  '& .stat-icon': {
                    transform: 'scale(1.1) rotate(5deg)',
                  },
                  '& .stat-progress': {
                    width: '100%',
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: stat.gradient,
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography 
                        variant="h3" 
                        component="div" 
                        sx={{ 
                          fontWeight: 700,
                          color: stat.color,
                          mb: 1,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography 
                        color="text.secondary" 
                        variant="body1"
                        sx={{ fontWeight: 500 }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                    <Box
                      className="stat-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 3,
                        background: stat.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <stat.icon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                  </Box>
                  <Box
                    className="stat-progress"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      height: '3px',
                      width: '60%',
                      background: stat.gradient,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Enhanced Main Action Button */}
      <Fade in timeout={1000}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<QrCodeScanner />}
            onClick={() => setShowQRScanner(true)}
            sx={{ 
              py: 3, 
              px: 6, 
              fontSize: '1.2rem',
              fontWeight: 600,
              borderRadius: 4,
              background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
              boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.4)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.5)}`,
                '&::before': {
                  transform: 'translateX(100%)',
                }
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.2)}, transparent)`,
                transition: 'transform 0.6s',
              }
            }}
          >
            <Biotech sx={{ mr: 1 }} />
            Scanner QR Code
            <Analytics sx={{ ml: 1 }} />
          </Button>
        </Box>
      </Fade>

      {/* Enhanced Instructions */}
      <Fade in timeout={1200}>
        <Alert 
          severity="info" 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.light, 0.1)})`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            '& .MuiAlert-icon': {
              color: theme.palette.info.main,
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            <strong>Instructions:</strong> Scannez le QR code de l'échantillon pour ouvrir automatiquement 
            le formulaire d'analyse chimique. Les résultats seront enregistrés et validés automatiquement.
          </Typography>
        </Alert>
      </Fade>

      {/* Modern Samples List */}
      <Fade in timeout={1400}>
        <Paper sx={{ 
          borderRadius: 4,
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          overflow: 'hidden',
        }}>
          <Box sx={{ 
            p: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <TrendingUp color="secondary" />
              Échantillons Prêts pour Analyse
              <Chip 
                label={samples.length} 
                color="secondary" 
                size="small"
                sx={{ 
                  fontWeight: 600,
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                }}
              />
            </Typography>
          </Box>
          
          {samples.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Speed sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                Aucun échantillon prêt pour analyse chimique
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Les échantillons préparés apparaîtront ici automatiquement
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {samples.map((sample, index) => (
                <React.Fragment key={sample.id}>
                  <Zoom in timeout={1600 + index * 100}>
                    <ListItem
                      sx={{ 
                        px: 3,
                        py: 2,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover': { 
                          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                          transform: 'translateX(8px)',
                          '& .sample-actions': {
                            opacity: 1,
                            transform: 'translateX(0)',
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: sample.priorite === Priority.URGENTE 
                            ? `linear-gradient(180deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
                            : sample.priorite === Priority.ELEVEE
                            ? `linear-gradient(180deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
                            : `linear-gradient(180deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        },
                        '&:hover::before': {
                          opacity: 1,
                        }
                      }}
                      onClick={() => handleSampleClick(sample)}
                    >
                      <ListItemIcon>
                        <Badge
                          color={getPriorityColor(sample.priorite as Priority)}
                          variant="dot"
                          invisible={sample.priorite === Priority.NORMALE}
                        >
                          <Avatar sx={{ 
                            background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                            width: 48,
                            height: 48,
                          }}>
                            <Science />
                          </Avatar>
                        </Badge>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="h6" fontWeight="600">
                              {sample.sample_id}
                            </Typography>
                            <Chip
                              icon={getPriorityIcon(sample.priorite as Priority)}
                              label={sample.priorite}
                              size="small"
                              color={getPriorityColor(sample.priorite as Priority)}
                              sx={{ fontWeight: 500 }}
                            />
                            <Chip
                              label={sample.type_echantillon}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        }
                        secondary={
                          <span>
                            <span style={{ display: 'block', marginBottom: '4px', color: 'rgba(0, 0, 0, 0.6)' }}>
                              <strong>Client:</strong> {sample.client?.company_name || 'N/A'}
                            </span>
                            <span style={{ display: 'block', marginBottom: '4px', color: 'rgba(0, 0, 0, 0.6)' }}>
                              <strong>Localisation:</strong> {sample.localisation_stockage || 'Laboratoire'}
                            </span>
                            <span style={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                              <strong>Préparé:</strong> {sample.date_fin_preparation ? new Date(sample.date_fin_preparation).toLocaleDateString('fr-FR') : 'N/A'}
                            </span>
                          </span>
                        }
                      />
                      
                      <Box 
                        className="sample-actions"
                        display="flex" 
                        gap={1}
                        sx={{
                          opacity: 0,
                          transform: 'translateX(20px)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <Tooltip title="Voir détails" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/samples/${sample.id}`);
                            }}
                            sx={{
                              background: alpha(theme.palette.info.main, 0.1),
                              '&:hover': {
                                background: alpha(theme.palette.info.main, 0.2),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <Visibility color="info" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Commencer analyse" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSampleClick(sample);
                            }}
                            sx={{
                              background: alpha(theme.palette.secondary.main, 0.1),
                              '&:hover': {
                                background: alpha(theme.palette.secondary.main, 0.2),
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <Science color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  </Zoom>
                  {index < samples.length - 1 && (
                    <Divider sx={{ 
                      mx: 3,
                      background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.5)}, transparent)`,
                    }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Fade>

      {/* Enhanced QR Scanner FAB */}
      <Zoom in timeout={2000}>
        <Fab
          color="secondary"
          aria-label="scan qr"
          sx={{ 
            position: 'fixed', 
            bottom: 24, 
            right: 24,
            width: 64,
            height: 64,
            background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.secondary.main, 0.4)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.1) rotate(5deg)',
              boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.5)}`,
            }
          }}
          onClick={() => setShowQRScanner(true)}
        >
          <QrCodeScanner sx={{ fontSize: 28 }} />
        </Fab>
      </Zoom>

      {/* QR Scanner Dialog */}
      {showQRScanner && (
        <WorkflowQRScanner
          mode="dialog"
          onClose={() => setShowQRScanner(false)}
          onSampleProcessed={handleQRScanSuccess}
        />
      )}

      {/* Analysis Form Dialog */}
      <Dialog
        open={showAnalysisForm}
        onClose={() => setShowAnalysisForm(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        {selectedSample && (
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Left Panel - Progressive Workflow Display */}
            <Box sx={{ 
              width: '40%', 
              p: 3, 
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`, 
              overflow: 'auto',
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            }}>
              <Typography variant="h5" sx={{ 
                mb: 3, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                fontWeight: 600,
              }}>
                <Science color="secondary" />
                Échantillon {selectedSample.sample_id}
              </Typography>
              <ProgressiveWorkflowDisplay
                sample={selectedSample}
                showEditButtons={false}
              />
            </Box>
            
            {/* Right Panel - Analysis Form */}
            <Box sx={{ width: '60%', overflow: 'auto' }}>
              <AnalysisForm
                initialData={{
                  sample_id: selectedSample.sample_id,
                  ...(selectedSample.metadonnees?.analysis_data || {})
                }}
                onSubmit={handleAnalysisCompleted}
                onCancel={() => setShowAnalysisForm(false)}
                analysts={analysts.map(analyst => ({
                  id: analyst.id,
                  name: `${analyst.firstName} ${analyst.lastName}`,
                  speciality: analyst.department
                }))}
              />
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default AnalystDashboard; 